import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getPixelValue } from "utilities/display";
import cx from "classnames";

import { titleCase } from "title-case";
import {
  ChevronRightIcon,
  CloseIcon,
  RacingIcon,
  ResizeIcon,
} from "@picklebet/ui";
import { TitleIcon } from "@/features/shell/components/shared/TitleIcon";
import { useTitle } from "@/hooks/graphql/useTitles";
import { navigate } from "utilities/navigate";

import * as styles from "./MultiModal.module.scss";

const PORTAL_ROOT =
  typeof document !== "undefined"
    ? document.getElementById("portal")
    : (null as HTMLElement);

const DEFAULT_HEIGHT = 540;

const totalAdditionalWindowHeight = getPixelValue(
  styles.totalAdditionalWindowHeight,
);
const modalPadding = getPixelValue(styles.totalAdditionalWindowHeight);

interface ratio {
  width: number;
  height: number;
}

const getEventXandY = (e: any): { x: number; y: number } => {
  if (e.touches || e.changedTouches) {
    const touch =
      (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]);
    if (touch) return { x: touch.clientX, y: touch.clientY };
  }
  return { x: e.clientX ?? 0, y: e.clientY ?? 0 };
};

const renderTitle = (title: string) => {
  const parts = title.split(/\s+vs?\.?\s+/i);
  if (parts.length === 2) {
    return (
      <>
        {titleCase(parts[0].toLowerCase())}{" "}
        <span className={styles.vsBadge}>VS</span>{" "}
        {titleCase(parts[1].toLowerCase())}
      </>
    );
  }
  return title;
};

const getZIndex = (element: any): number => {
  if (!element || element === document) return 0;
  const z = window.document.defaultView
    .getComputedStyle(element)
    .getPropertyValue("z-index");
  if (z === "auto") return getZIndex(element.parentNode);
  return Number(z);
};

const findHighestZIndex = (): number => {
  const elems = document.querySelectorAll("[class*=MultiModal]");
  let highest = 0;
  for (let i = 0; i < elems.length; i++) {
    const zindex = getZIndex(elems[i]);
    if (zindex > highest) highest = zindex;
  }
  return highest + 1;
};

// For Desktop use only. Supports Resize, Drag-Move
const MultiModal = ({
  children,
  width = 960 + modalPadding,
  height = DEFAULT_HEIGHT,
  x = window.innerWidth / 2 - width / 2,
  y = window.innerHeight / 2 - height / 2,
  title,
  sport,
  isLive,
  eventUrl,
  maxSize = 80,
  minWidth = 320,
  minHeight = 180,
  ratio,
  onClose,
  subtitle,
  isLiveRacingChannel,
}: {
  // See Defaults Above
  children: React.ReactNode;
  title: string;
  sport?: string;
  isLive?: boolean;
  eventUrl?: string;
  width?: number;
  height?: number;
  x?: number;
  y?: number;
  maxSize?: number;
  minWidth?: number;
  minHeight?: number;
  ratio?: ratio;
  onClose: () => void;
  subtitle?: React.ReactNode;
  isLiveRacingChannel?: boolean;
}) => {
  const sportTitle = useTitle(sport ?? "");
  const el = useRef(document.createElement("div"));
  const refModal = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const initialHeight =
    (ratio ? (width / ratio.width) * ratio.height : height) +
    totalAdditionalWindowHeight;

  const [state, setState] = useState({
    width,
    height: initialHeight,
    x,
    y,
    zIndex: 1000,
    notification: "",
  });

  // Mutable ref for transient drag/resize state — avoids re-renders during move
  const dragRef = useRef({
    isDragging: false,
    isResizing: false,
    rel: { x: 0, y: 0 },
    x: state.x,
    y: state.y,
    width: state.width,
    height: state.height,
    zIndex: state.zIndex,
  });

  // Refs for props used in stable callbacks (avoids recreating callbacks when props change)
  const ratioRef = useRef(ratio);
  ratioRef.current = ratio;
  const configRef = useRef({ maxSize, minWidth, minHeight });
  configRef.current = { maxSize, minWidth, minHeight };

  // Keep dragRef in sync when React state changes (on mouseup sync)
  useEffect(() => {
    const d = dragRef.current;
    d.x = state.x;
    d.y = state.y;
    d.width = state.width;
    d.height = state.height;
    d.zIndex = state.zIndex;
  }, [state.x, state.y, state.width, state.height, state.zIndex]);

  const setIsDragging = (e: any) => {
    const d = dragRef.current;
    if (d.isResizing || d.isDragging) return;
    const { x, y } = getEventXandY(e);
    d.isDragging = true;
    d.rel = {
      x: x - refModal.current.offsetLeft,
      y: y - refModal.current.offsetTop,
    };
    d.zIndex = findHighestZIndex();
    refModal.current.style.zIndex = `${d.zIndex}`;
    setState((prev) =>
      prev.notification ? { ...prev, notification: "" } : prev,
    );
  };

  const setIsResizing = () => {
    const d = dragRef.current;
    if (d.isResizing || d.isDragging) return;
    d.isResizing = true;
    d.zIndex = findHighestZIndex();
    refModal.current.style.zIndex = `${d.zIndex}`;
    if (overlayRef.current) overlayRef.current.style.display = "block";
    setState((prev) =>
      prev.notification ? { ...prev, notification: "" } : prev,
    );
  };

  // Stable callback — reads from refs, no React state deps
  const onMove = useCallback((e: any) => {
    const d = dragRef.current;
    const { x, y } = getEventXandY(e);

    if (d.isDragging) {
      const newX = x - d.rel.x;
      const newY = y - d.rel.y;
      if (newX >= 0 && newX + d.width <= window.innerWidth) d.x = newX;
      if (newY >= 0 && newY + d.height <= window.innerHeight) d.y = newY;

      if (refModal.current) {
        refModal.current.style.left = `${d.x}px`;
        refModal.current.style.top = `${d.y}px`;
      }
      e.stopPropagation();
      e.preventDefault();
    } else if (d.isResizing) {
      const r = ratioRef.current;
      const cfg = configRef.current;
      const newWidth = x - refModal.current.offsetLeft;
      const newHeight = y - refModal.current.offsetTop;

      const windowSize = window.innerWidth * window.innerHeight;
      const modalSize = newWidth * newHeight;
      const withinMax = (modalSize / windowSize) * 100 < cfg.maxSize;

      if (withinMax) {
        if (newWidth > cfg.minWidth && newHeight >= cfg.minHeight) {
          if (r) {
            d.width = newWidth;
            d.height =
              (newWidth / r.width) * r.height + totalAdditionalWindowHeight;
          } else {
            d.width = newWidth;
            d.height = newHeight;
          }
          if (refModal.current) {
            refModal.current.style.width = `${d.width}px`;
            refModal.current.style.height = `${d.height}px`;
          }
          e.stopPropagation();
          e.preventDefault();
        } else {
          d.isResizing = false;
          d.isDragging = false;
          d.width += 20;
          d.height += 20;
          if (overlayRef.current) overlayRef.current.style.display = "none";
          setState((prev) => ({
            ...prev,
            width: d.width,
            height: d.height,
            notification: "Min modal screen size reached!",
          }));
        }
      } else {
        d.isResizing = false;
        d.isDragging = false;
        if (overlayRef.current) overlayRef.current.style.display = "none";
        setState((prev) => ({
          ...prev,
          notification: "Max modal screen size reached!",
        }));
      }
    }
  }, []);

  // Stable callback — syncs ref state back to React on release
  const onUp = useCallback(() => {
    const d = dragRef.current;
    if (d.isDragging || d.isResizing) {
      d.isDragging = false;
      d.isResizing = false;
      if (overlayRef.current) overlayRef.current.style.display = "none";
      setState((prev) => ({
        ...prev,
        x: d.x,
        y: d.y,
        width: d.width,
        height: d.height,
        zIndex: d.zIndex,
      }));
    }
  }, []);

  // Register move/up listeners once (callbacks are stable)
  useEffect(() => {
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp, { passive: false });

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
    };
  }, [onMove, onUp]);

  // Add Modal to Portal Dom
  useEffect(() => {
    const current = el.current;
    PORTAL_ROOT?.appendChild(current);

    return () => {
      PORTAL_ROOT?.removeChild(current);
    };
  }, []);

  const ModalFrame = (): React.ReactNode => {
    return (
      <div
        style={{
          width: `${state.width}px`,
          height: `${state.height}px`,
          left: `${state.x}px`,
          top: `${state.y}px`,
          zIndex: state.zIndex,
        }}
        className={styles.multiModal}
        ref={refModal}
      >
        <div
          className={styles.title}
          title={title}
          onMouseDown={setIsDragging}
          onTouchStart={setIsDragging}
        >
          {sportTitle && (
            <div className={styles.sportIconWrapper}>
              {isLiveRacingChannel ? (
                <RacingIcon className={styles.sportIcon} />
              ) : (
                <TitleIcon title={sportTitle} className={styles.sportIcon} />
              )}
              {isLive && <div className={styles.liveDot} />}
            </div>
          )}
          <div className={styles.titleWrapper}>
            <div className={styles.titleText}>
              {renderTitle(title)}
              {eventUrl && (
                <div
                  className={styles.eventLink}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(eventUrl);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <ChevronRightIcon />
                </div>
              )}
            </div>
            {subtitle}
          </div>
          <button
            className={styles.closeButton}
            onClick={onClose}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <CloseIcon />
          </button>
        </div>
        <div className={styles.content}>{children}</div>
        <div
          ref={overlayRef}
          className={styles.overlay}
          style={{ display: "none" }}
        />
        <div
          className={styles.resizer}
          onMouseDown={setIsResizing}
          onTouchStart={setIsResizing}
        >
          <ResizeIcon className={styles.resizeIcon} />
        </div>
        <div
          className={cx(styles.notificationArea, {
            [styles.active]: state.notification !== "",
          })}
        >
          <div className={styles.notification}>{state.notification}</div>
        </div>
      </div>
    );
  };

  return createPortal(ModalFrame(), el.current);
};

export { MultiModal };
